// Reports: paste results one after another and each becomes a block laid out on its own (figure, tiles, bars,
// pivot, line or table), with a heading and a note. The editor is the page itself (.rp, web/report.css), so what
// you see is what the exported file and the PDF show. The app's chrome around it is in web/reports.css.
import { renderText } from "../report";
import { reportMeta, shareReportText } from "../sqlfile";
import { VIEW_NAMES, type Settings, type View } from "../layout";
import { ago, api, clipboardFiles, esc, icon, isTyping, pastedFile, setPageMeta, toast, upload, writeClipboard } from "./util";

type Summary = { id: number; title: string; intro: string; report_date: string; created_at: string; updated_at: string; blocks: number };
type Block = { id: number; report_id: number; position: number; heading: string; note: string; settings: Settings; name: string; type: string; size: number; text: string | null };
type Full = Omit<Summary, "blocks"> & { blocks: Block[] };
type Hooks = { leaveQuery: () => Promise<void>; showEmpty: () => void; renderList: () => void };

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const el = {
  editor: $("report"), title: $<HTMLInputElement>("rTitle"), date: $<HTMLInputElement>("rDate"), intro: $<HTMLTextAreaElement>("rIntro"),
  blocks: $("rBlocks"), drop: $("rDrop"), paper: $("rPaper"), status: $("rStatus"), del: $("rDel"), empty: $("empty"),
};

export let list: Summary[] = [];
let report: Full | null = null;
let hooks: Hooks;

export const openId = () => report?.id ?? null;
export async function load() { list = await api<Summary[]>("/api/reports"); }

/* ---------- sidebar ---------- */
export function listHtml(term: string) {
  const shown = list.filter((r) => !term || [r.title, r.intro].some((f) => f.toLowerCase().includes(term)));
  return shown.map((r) => `<button class="item${r.id === report?.id ? " active" : ""}" data-report="${r.id}">
      <span class="t"><span class="name">${esc(r.title)}</span><span class="when">${ago(r.updated_at)}</span></span>
      <span class="s">${esc(r.intro.split("\n").find((l) => l.trim())?.trim() ?? "") || "&nbsp;"}</span>
      <span class="m"><span class="sm">${icon("table")}${r.blocks} block${r.blocks === 1 ? "" : "s"}</span><span class="tg">${esc(r.report_date)}</span></span>
    </button>`).join("") || `<div class="none">${list.length ? "No report matches." : "No reports yet. Press + to start one."}</div>`;
}

/* ---------- open / close ---------- */
export async function open(id: number) {
  await flush();
  await hooks.leaveQuery();
  let r: Full;
  try { r = await api<Full>(`/api/reports/${id}`); }
  catch { toast("That report doesn't exist any more"); await close(); return hooks.showEmpty(); }
  const same = report?.id === id;
  report = r;
  el.empty.hidden = true;
  el.editor.hidden = false;
  el.title.value = r.title;
  el.date.value = r.report_date;
  el.intro.value = r.intro;
  fit(el.intro);
  disarm();
  setStatus("Saved");
  renderBlocks();
  history.replaceState(null, "", `/r/${id}`);
  setPageMeta(reportMeta({ ...r, blocks: r.blocks.length }));
  hooks.renderList();
  if (!same) document.querySelector(".main")!.scrollTop = 0;
}

/** Leaves the report (saving first); the caller shows what comes next. */
export async function close() {
  await flush();
  report = null;
  el.editor.hidden = true;
}

export async function create() {
  const r = await api<Summary>("/api/reports", { method: "POST", body: JSON.stringify({ title: "Untitled report" }) });
  await load();
  await open(r.id);
  el.title.focus();
  el.title.select();
}

/* ---------- blocks ---------- */
const plans = new Map<number, ReturnType<typeof renderText>>();
const editingData = new Set<number>(); // blocks whose raw data is open for fixing by hand

/** The block's drawing, and the options that fit it (view, which number, totals, details). */
function bodyAndOptions(b: Block): { body: string; opts: string } {
  if (b.type.startsWith("image/")) {
    return { body: `<img class="rp-img" src="/api/blocks/${b.id}" alt="${esc(b.heading || b.name)}" />`, opts: `<span class="rb-kind">${icon("image")}Screenshot</span>` };
  }
  const r = renderText(b.text ?? "", b.name, b.settings);
  plans.set(b.id, r);
  const p = r.plan;
  if (!p) return { body: r.html, opts: `<span class="rb-kind" title="Not read as a table. Edit data to fix it.">${icon("text")}Text</span>` };
  const measures = p.columns.flatMap((c, k) => (c.kind === "number" ? [k] : []));
  const usesMeasure = ["bars", "pivot", "line", "table", "figure"].includes(p.view) && measures.length > 1;
  const usesTotals = ["bars", "pivot", "table"].includes(p.view) && (!!p.given || p.columns.some((c) => c.kind === "number" && c.additive));
  const opts = `<div class="rb-views" role="group" aria-label="Show as">${p.views.map((v) =>
    `<button data-view="${v}" aria-pressed="${v === p.view}" title="${v === p.auto ? "Picked for this result" : `Show as ${VIEW_NAMES[v].toLowerCase()}`}">${VIEW_NAMES[v]}${v === p.auto ? `<span class="rb-auto" aria-hidden="true"></span>` : ""}</button>`).join("")}</div>
    ${usesMeasure ? `<label class="rb-field" title="The number the chart draws">${icon("chart")}<select data-measure>${measures.map((k) =>
      `<option value="${k}"${k === p.measure ? " selected" : ""}>${esc(p.columns[k]!.name)}</option>`).join("")}</select></label>` : ""}
    ${usesTotals ? `<label class="rb-check" title="${p.given ? "The total row from the result itself" : "Sums of the number columns"}"><input type="checkbox" data-totals${p.totals ? " checked" : ""} />Totals</label>` : ""}
    ${r.meta ? `<label class="rb-check" title="The lines about the report that came above the table"><input type="checkbox" data-details${b.settings.details !== false ? " checked" : ""} />Details</label>` : ""}`;
  return { body: r.html, opts };
}

function blockHtml(b: Block, i: number, n: number) {
  const { body, opts } = bodyAndOptions(b);
  const text = !b.type.startsWith("image/");
  const open = editingData.has(b.id);
  return `<section class="rp-block rb${open ? " editing-data" : ""}" data-id="${b.id}">
    <div class="rb-tools">
      <span class="rb-handle" draggable="true" title="Drag to move" aria-hidden="true">${icon("grip")}</span>
      <span class="rb-opts">${opts}</span>
      <span class="spacer"></span>
      ${text ? `<button class="rb-btn${open ? " on" : ""}" data-act="data" title="Fix the data by hand: rename a column, delete a line, correct a value">${icon(open ? "check" : "edit")}<span>${open ? "Done" : "Edit data"}</span></button>` : ""}
      <button class="rb-btn" data-act="up" title="Move up" aria-label="Move up"${i === 0 ? " disabled" : ""}>${icon("chev", "i up")}</button>
      <button class="rb-btn" data-act="down" title="Move down" aria-label="Move down"${i === n - 1 ? " disabled" : ""}>${icon("chev")}</button>
      <button class="rb-btn danger" data-act="del" title="Remove from the report">${icon("trash")}<span></span></button>
    </div>
    <input class="rp-h2 rp-in" data-f="heading" value="${esc(b.heading)}" placeholder="Heading" aria-label="Heading" />
    <textarea class="rp-note rp-in" data-f="note" rows="1" placeholder="Note: what to notice, where it comes from (optional)" aria-label="Note">${esc(b.note)}</textarea>
    ${open ? `<textarea class="rb-data" spellcheck="false" aria-label="Data" placeholder="Paste or type the result here">${esc(b.text ?? "")}</textarea>
      <p class="rb-data-hint">Changes show below as you type. The first line with the most columns is read as the header; lines above it become details.</p>` : ""}
    <div class="rp-body">${body}</div>
  </section>`;
}

function renderBlocks() {
  if (!report) return;
  const n = report.blocks.length;
  plans.clear();
  el.blocks.innerHTML = report.blocks.map((b, i) => blockHtml(b, i, n)).join("");
  el.drop.classList.toggle("small", n > 0);
  for (const t of el.blocks.querySelectorAll<HTMLTextAreaElement>("textarea")) fit(t);
}
function redrawBlock(id: number) {
  if (!report) return;
  const i = report.blocks.findIndex((b) => b.id === id);
  const node = el.blocks.querySelector(`[data-id="${id}"]`);
  if (i < 0 || !node) return;
  const tpl = document.createElement("template");
  tpl.innerHTML = blockHtml(report.blocks[i]!, i, report.blocks.length).trim();
  node.replaceWith(tpl.content);
  for (const t of el.blocks.querySelectorAll<HTMLTextAreaElement>(`[data-id="${id}"] textarea`)) fit(t);
}
/** Redraws only the drawing and its options, so the field being typed in keeps its focus. */
function refreshBody(b: Block) {
  const node = el.blocks.querySelector(`[data-id="${b.id}"]`);
  if (!node) return;
  const { body, opts } = bodyAndOptions(b);
  node.querySelector(".rp-body")!.innerHTML = body;
  node.querySelector(".rb-opts")!.innerHTML = opts;
}

const fit = (t: HTMLTextAreaElement) => { t.style.height = "auto"; t.style.height = `${t.scrollHeight}px`; };

export async function addFiles(files: File[]) {
  if (!report || !files.length) return;
  const id = report.id;
  const added = await upload<Block[]>(`/api/reports/${id}/blocks`, files);
  if (!added || report?.id !== id) return;
  report.blocks.push(...added);
  renderBlocks();
  await load();
  hooks.renderList();
  el.blocks.querySelector(`[data-id="${added[0]!.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  toast(added.length > 1 ? `Added ${added.length} blocks` : "Added to the report");
}

/* ---------- saving: report fields and block fields each save shortly after typing stops ---------- */
const pending = new Map<string, () => Promise<unknown>>();
let timer: ReturnType<typeof setTimeout> | undefined;
function setStatus(s: string, cls = "") {
  el.status.textContent = s;
  el.status.className = `status ${cls}`;
}
function queue(key: string, run: () => Promise<unknown>) {
  pending.set(key, run);
  setStatus("Unsaved changes", "dirty");
  clearTimeout(timer);
  timer = setTimeout(flush, 700);
}
let inflight: Promise<void> | null = null;
/** Saves everything queued; waits for a save already under way, and keeps edits whose save failed. */
export async function flush(): Promise<void> {
  clearTimeout(timer);
  if (inflight) await inflight;
  if (!pending.size) return;
  const runs = [...pending];
  pending.clear();
  inflight = (async () => {
    const results = await Promise.allSettled(runs.map(([, run]) => run()));
    const failed = runs.filter((_, i) => results[i]!.status === "rejected");
    for (const [key, run] of failed) if (!pending.has(key)) pending.set(key, run);
    if (failed.length) {
      setStatus("Save failed", "error");
      toast(`Couldn't save: ${(results.find((r) => r.status === "rejected") as PromiseRejectedResult).reason}`);
      return;
    }
    if (!pending.size) setStatus("Saved");
    await load().catch(() => {});
    hooks.renderList();
  })();
  try { await inflight; } finally { inflight = null; }
}
function saveReport() {
  if (!report) return;
  const id = report.id;
  const body = { title: el.title.value, intro: el.intro.value, report_date: el.date.value };
  Object.assign(report, { title: body.title, intro: body.intro, report_date: body.report_date || report.report_date });
  setPageMeta(reportMeta({ ...report, blocks: report.blocks.length }));
  queue("report", () => api(`/api/reports/${id}`, { method: "PUT", body: JSON.stringify(body), keepalive: true }));
}
function saveData(b: Block, text: string) {
  b.text = text;
  queue(`data${b.id}`, () => api(`/api/blocks/${b.id}`, { method: "PUT", body: JSON.stringify({ text }) }));
}
function saveBlock(b: Block, patch: Partial<Pick<Block, "heading" | "note" | "settings">>) {
  Object.assign(b, patch);
  const body = { heading: b.heading, note: b.note, settings: b.settings };
  queue(`block${b.id}`, () => api(`/api/blocks/${b.id}`, { method: "PUT", body: JSON.stringify(body), keepalive: true }));
}

/* ---------- order ---------- */
async function move(from: number, to: number) {
  if (!report || to < 0 || to >= report.blocks.length || from === to) return;
  const [b] = report.blocks.splice(from, 1);
  report.blocks.splice(to, 0, b!);
  renderBlocks();
  el.blocks.querySelector(`[data-id="${b!.id}"]`)?.classList.add("moved");
  await api(`/api/reports/${report.id}/order`, { method: "PUT", body: JSON.stringify({ ids: report.blocks.map((x) => x.id) }) });
}

/* ---------- delete: click twice, like queries ---------- */
let armed: { id: number | "report"; t: ReturnType<typeof setTimeout> } | null = null;
function disarm() {
  if (armed) clearTimeout(armed.t);
  armed = null;
  el.editor.querySelectorAll(".armed").forEach((b) => { b.classList.remove("armed"); const s = b.querySelector("span"); if (s) s.textContent = ""; });
}
function arm(id: number | "report", btn: HTMLElement, label: string) {
  disarm();
  btn.classList.add("armed");
  btn.querySelector("span")!.textContent = label;
  armed = { id, t: setTimeout(disarm, 3000) };
}
async function removeBlock(b: Block, btn: HTMLElement) {
  if (armed?.id !== b.id) return arm(b.id, btn, "Click again to remove");
  disarm();
  pending.delete(`block${b.id}`);
  pending.delete(`data${b.id}`);
  editingData.delete(b.id);
  await api(`/api/blocks/${b.id}`, { method: "DELETE" });
  report!.blocks = report!.blocks.filter((x) => x.id !== b.id);
  renderBlocks();
  await load();
  hooks.renderList();
  toast("Block removed");
}
async function removeReport() {
  if (!report) return;
  if (armed?.id !== "report") return arm("report", el.del, "Click again to delete");
  disarm();
  pending.clear();
  await api(`/api/reports/${report.id}`, { method: "DELETE" });
  report = null;
  el.editor.hidden = true;
  await load();
  toast("Report deleted");
  hooks.showEmpty();
}

/* ---------- export ---------- */
async function exportReport(print: boolean) {
  if (!report) return;
  const url = `/api/reports/${report.id}/export.html${print ? "?print=1" : ""}`;
  // open the tab now, while the click still counts as the user's, then point it at the file once saved
  const tab = print ? window.open("about:blank", "_blank") : null;
  await flush();
  if (print) {
    if (tab) tab.location.href = url;
    else location.href = url;
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    document.body.append(a);
    a.click();
    a.remove();
  }
}

/* ---------- "Add to report" on a query's sample ---------- */
const picker = $("reportPicker");
let pickSample: number | null = null;
export async function pickFor(sampleId: number, anchor: HTMLElement) {
  if (!picker.hidden && pickSample === sampleId) return closePicker();
  pickSample = sampleId;
  await load().catch(() => {});
  picker.innerHTML = `<div class="sp-label">Add this sample to</div>
    <div class="rpk-list">${list.slice(0, 12).map((r) => `<button class="sp-look rpk-item" data-pick="${r.id}"><span>${esc(r.title)}</span><small>${r.blocks} block${r.blocks === 1 ? "" : "s"} · ${ago(r.updated_at)}</small></button>`).join("")}</div>
    <button class="sp-look rpk-item new" data-pick="new">${icon("plus")}<span>New report</span></button>`;
  const box = anchor.getBoundingClientRect();
  picker.hidden = false;
  const w = picker.offsetWidth;
  const h = picker.offsetHeight;
  picker.style.left = `${Math.max(8, Math.min(box.right - w, innerWidth - w - 8))}px`;
  // below the button if it fits, otherwise above it
  picker.style.top = `${box.bottom + 6 + h > innerHeight - 8 ? Math.max(8, box.top - 6 - h) : box.bottom + 6}px`;
  picker.querySelector<HTMLElement>("button")?.focus({ preventScroll: true });
}
function closePicker() { picker.hidden = true; pickSample = null; }
picker.addEventListener("click", async (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>("[data-pick]");
  if (!b || pickSample === null) return;
  const sample = pickSample;
  closePicker();
  let id: number, title: string;
  if (b.dataset.pick === "new") {
    const r = await api<Summary>("/api/reports", { method: "POST", body: JSON.stringify({ title: "Untitled report" }) });
    id = r.id; title = r.title;
  } else {
    id = Number(b.dataset.pick);
    title = list.find((r) => r.id === id)?.title ?? "the report";
  }
  await api(`/api/reports/${id}/blocks`, { method: "POST", body: JSON.stringify({ sample }) });
  await load();
  hooks.renderList();
  toast(`Added to “${title}”. Open it from Reports in the sidebar.`);
});
document.addEventListener("click", (e) => {
  if (!picker.hidden && !e.composedPath().includes(picker) && !(e.target as HTMLElement).closest("[data-to-report]")) closePicker();
});
document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !picker.hidden) closePicker(); }, true);

/* ---------- wiring ---------- */
export function init(h: Hooks) {
  hooks = h;
  for (const f of [el.title, el.intro]) f.addEventListener("input", saveReport);
  el.date.addEventListener("change", saveReport);
  el.intro.addEventListener("input", () => fit(el.intro));
  el.del.onclick = removeReport;
  $("rShare").onclick = async () => {
    if (!report) return;
    const url = `${location.origin}/r/${report.id}`;
    const text = shareReportText({ ...report, blocks: report.blocks.length }, url);
    toast((await writeClipboard(text)) ? "Copied report title, intro & link." : `Couldn't copy: ${url}`);
  };
  $("rPdf").onclick = () => exportReport(true);
  $("rHtml").onclick = () => exportReport(false);
  $("rPaste").onclick = async () => { const f = await clipboardFiles(); if (f) addFiles(f); };
  $<HTMLInputElement>("rFile").onchange = (e) => {
    const input = e.target as HTMLInputElement;
    addFiles([...(input.files ?? [])]);
    input.value = "";
  };

  const blockOf = (t: EventTarget | null) => {
    const id = Number((t as HTMLElement).closest<HTMLElement>(".rb")?.dataset.id);
    return report?.blocks.find((b) => b.id === id) ?? null;
  };
  let dataTimer: ReturnType<typeof setTimeout> | undefined;
  el.blocks.addEventListener("input", (e) => {
    const t = e.target as HTMLInputElement | HTMLTextAreaElement;
    const b = blockOf(t);
    if (b && t.classList.contains("rb-data")) {
      fit(t as HTMLTextAreaElement);
      saveData(b, t.value);
      clearTimeout(dataTimer);
      dataTimer = setTimeout(() => refreshBody(b), 200);
      return;
    }
    if (!b || !t.dataset.f) return;
    if (t instanceof HTMLTextAreaElement) fit(t);
    saveBlock(b, { [t.dataset.f]: t.value });
  });
  el.blocks.addEventListener("change", (e) => {
    const t = e.target as HTMLInputElement | HTMLSelectElement;
    const b = blockOf(t);
    if (!b) return;
    if (t.matches("[data-measure]")) saveBlock(b, { settings: { ...b.settings, measure: Number(t.value) } });
    else if (t.matches("[data-totals]")) saveBlock(b, { settings: { ...b.settings, totals: (t as HTMLInputElement).checked } });
    else if (t.matches("[data-details]")) saveBlock(b, { settings: { ...b.settings, details: (t as HTMLInputElement).checked } });
    else return;
    redrawBlock(b.id);
  });
  el.blocks.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const b = blockOf(t);
    if (!b || !report) return;
    const view = t.closest<HTMLElement>("[data-view]")?.dataset.view as View | undefined;
    if (view) {
      const p = plans.get(b.id)?.plan;
      saveBlock(b, { settings: { ...b.settings, view: view === p?.auto ? "auto" : view } });
      return redrawBlock(b.id);
    }
    const act = t.closest<HTMLElement>("[data-act]");
    if (!act) return;
    const i = report.blocks.indexOf(b);
    if (act.dataset.act === "data") {
      if (editingData.has(b.id)) editingData.delete(b.id);
      else editingData.add(b.id);
      redrawBlock(b.id);
      el.blocks.querySelector<HTMLTextAreaElement>(`[data-id="${b.id}"] .rb-data`)?.focus();
      return;
    }
    if (act.dataset.act === "up") move(i, i - 1);
    else if (act.dataset.act === "down") move(i, i + 1);
    else if (act.dataset.act === "del") removeBlock(b, act);
  });

  // drag a block by its handle to move it; drop files (or a screenshot) anywhere on the page to add them
  let dragging: number | null = null;
  const mark = (target: HTMLElement | null, after = false) => {
    el.blocks.querySelectorAll(".drop-before, .drop-after").forEach((x) => x.classList.remove("drop-before", "drop-after"));
    target?.classList.add(after ? "drop-after" : "drop-before");
  };
  const spot = (e: DragEvent) => {
    const node = (e.target as HTMLElement).closest<HTMLElement>(".rb");
    if (!node) return null;
    const box = node.getBoundingClientRect();
    return { node, after: e.clientY > box.top + box.height / 2 };
  };
  el.blocks.addEventListener("dragstart", (e) => {
    const node = (e.target as HTMLElement).closest<HTMLElement>(".rb");
    if (!node || !(e.target as HTMLElement).closest(".rb-handle")) return;
    dragging = Number(node.dataset.id);
    node.classList.add("dragging");
    e.dataTransfer!.effectAllowed = "move";
    e.dataTransfer!.setData("text/plain", "");
    e.dataTransfer!.setDragImage(node, 24, 24);
  });
  el.blocks.addEventListener("dragend", () => {
    dragging = null;
    mark(null);
    el.blocks.querySelector(".dragging")?.classList.remove("dragging");
  });
  let depth = 0;
  const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes("Files");
  el.paper.addEventListener("dragenter", (e) => { if (hasFiles(e)) { depth++; el.paper.classList.add("dragging"); } });
  el.paper.addEventListener("dragleave", (e) => { if (hasFiles(e) && --depth <= 0) { depth = 0; el.paper.classList.remove("dragging"); } });
  el.paper.addEventListener("dragover", (e) => {
    if (hasFiles(e)) return e.preventDefault();
    if (dragging === null) return;
    e.preventDefault();
    const s = spot(e);
    mark(s?.node ?? null, s?.after);
  });
  el.paper.addEventListener("drop", (e) => {
    e.preventDefault();
    if (hasFiles(e)) {
      depth = 0;
      el.paper.classList.remove("dragging");
      return addFiles([...(e.dataTransfer?.files ?? [])]);
    }
    if (dragging === null || !report) return;
    const s = spot(e);
    mark(null);
    if (!s) return;
    const from = report.blocks.findIndex((b) => b.id === dragging);
    let to = report.blocks.findIndex((b) => b.id === Number(s.node.dataset.id)) + (s.after ? 1 : 0);
    if (from < to) to--;
    move(from, to);
  });

  // ⌘V anywhere outside a text field adds what's on the clipboard as a new block
  document.addEventListener("paste", (e) => {
    if (!report || el.editor.hidden || isTyping(e.target)) return;
    const files = [...(e.clipboardData?.files ?? [])];
    const text = e.clipboardData?.getData("text/plain") ?? "";
    if (!files.length && !text.trim()) return;
    e.preventDefault();
    addFiles(files.length ? files : [pastedFile(text)]);
  });
  window.addEventListener("pagehide", () => { flush(); });
}
